import { useMemo, useCallback } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import {
  TasksService,
  type TaskListQuery,
  type Task,
  TaskAssigneeType,
} from 'src/api/generated';
import type { TaskFiltersState } from '../../types/agenda.types';

interface UseAgendaTasksListParams {
  filters?: TaskFiltersState;
  enabled?: boolean;
}

interface UseAgendaTasksListResult {
  tasks: Task[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<unknown>;
  loadMore: () => void;
  hasNextPage: boolean;
  loadingMore: boolean;
}

export const useAgendaTasksList = (
  params: UseAgendaTasksListParams = {}
): UseAgendaTasksListResult => {
  const { filters = {}, enabled = true } = params;

  const fetchTasks = async ({
    pageParam,
  }: {
    pageParam: string | undefined;
  }) => {
    const fetchParams: TaskListQuery = {
      limit: 100,
    };

    // Apply filters
    if (filters.status) fetchParams.status = filters.status;
    if (filters.assignedToType) {
      fetchParams.assignedToType =
        filters.assignedToType === TaskAssigneeType.USER
          ? TaskAssigneeType.USER
          : TaskAssigneeType.AI_EMPLOYEE;
    }
    if (filters.assignedToId) fetchParams.assignedToId = filters.assignedToId;
    if (filters.dueBefore) fetchParams.dueBefore = filters.dueBefore;
    if (filters.dueAfter) fetchParams.dueAfter = filters.dueAfter;

    if (filters.conversationId && typeof filters.conversationId === 'number') {
      fetchParams.conversationId = filters.conversationId;
    }

    return await TasksService.getTasks(
      fetchParams.limit,
      pageParam,
      fetchParams.dueBefore,
      fetchParams.dueAfter,
      fetchParams.conversationId,
      fetchParams.assignedToType,
      fetchParams.assignedToId,
      fetchParams.status
    );
  };

  const {
    data,
    error,
    isLoading,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['agenda-tasks-list', filters],
    queryFn: fetchTasks,
    getNextPageParam: (lastPage) => lastPage.nextPageToken,
    initialPageParam: undefined,
    enabled,
  });

  const tasks = useMemo(() => {
    if (!data?.pages) return [];

    const allTasks = data.pages.flatMap((page) => page.items);

    // Apply client-side filtering for 'none' conversation filter
    if (filters.conversationId === 'none') {
      return allTasks.filter((task) => !task.context.conversationId);
    }

    return allTasks;
  }, [data?.pages, filters.conversationId]);

  const loadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return {
    tasks,
    isLoading,
    error,
    refetch,
    loadMore,
    hasNextPage: hasNextPage || false,
    loadingMore: isFetchingNextPage,
  };
};
